import React, { useMemo, useState } from 'react';
import { MapPinIcon, TrophyIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { SectionHeading } from '../components/SectionHeading';
import { EVENTS, EVENT_CATEGORIES, RESULTS, type EventCategory } from '../data/calendar';
type Filter = EventCategory | 'all';
const CATEGORY_STYLES: Record<EventCategory, string> = {
  competition: 'border-crimson/50 text-crimson',
  adherents: 'border-gold/50 text-gold',
  autres: 'border-white/20 text-bone'
};
export function Calendrier() {
  const [filter, setFilter] = useState<Filter>('all');
  const events = useMemo(() => filter === 'all' ? EVENTS : EVENTS.filter(event => event.category === filter), [filter]);
  const filters: {
    id: Filter;
    label: string;
  }[] = [{
    id: 'all',
    label: 'Tous les évènements'
  }, ...EVENT_CATEGORIES.map(c => ({
    id: c.id as Filter,
    label: c.label
  }))];
  return <>
      <PageHeader kanji="予定" eyebrow="Calendrier" title="La saison 2026-2027" intro="Compétitions, évènements réservés aux adhérents, stages et démonstrations ouvertes au public : tous les rendez-vous du club au même endroit." data-id="element-99" data-mp="1owo5836i8bbl" />

      <section className="mx-auto max-w-shell px-5 py-14 lg:px-8 lg:py-20" data-id="element-100" data-mp="159yflpn7ekwx">
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrer les évènements" data-id="element-101" data-mp="10bwvr2hyehke">
          {filters.map(item => {
          const active = filter === item.id;
          return <button key={item.id} type="button" onClick={() => setFilter(item.id)} aria-pressed={active} className={`zk-focus border px-4 py-2 font-display text-xs uppercase tracking-[0.14em] transition-colors duration-150 ease-out ${active ? 'border-gold bg-gold text-ink' : 'border-white/15 text-muted hover:border-gold/60 hover:text-gold'}`} data-id="element-102" data-mp="qm3izy62rwek">
                {item.label}
              </button>;
        })}
        </div>

        {events.length === 0 ? <p className="mt-12 border border-white/10 bg-panel p-10 text-center text-sm text-muted" data-id="element-103" data-mp="301bz979f4q3">
            Aucun évènement programmé dans cette catégorie pour le moment.
          </p> : <ul className="mt-10 divide-y divide-gold/10 border-y border-gold/10" data-id="element-104" data-mp="3p66kzo238gyt">
            {events.map(event => {
          const category = EVENT_CATEGORIES.find(c => c.id === event.category);
          return <li key={event.title} className="grid gap-4 py-7 md:grid-cols-[6rem_1fr_10rem] md:items-start md:gap-8" data-id="element-105" data-mp="7xjcqcb4idn0">
                  <p className="font-display leading-none" data-id="element-106" data-mp="bymr0udiw1m4">
                    <span className="block text-3xl font-bold text-gold" data-id="element-107" data-mp="bx1vb1fh2cmo">{event.day}</span>
                    <span className="mt-1 block text-xs uppercase tracking-[0.18em] text-muted" data-id="element-108" data-mp="znfsf6cwz1o4">{event.month}</span>
                  </p>
                  <div data-id="element-109" data-mp="2p5l394q0dlbx">
                    <h2 className="font-display text-lg uppercase tracking-[0.04em] text-bone" data-id="element-110" data-mp="2p4t45jx7d5gw">{event.title}</h2>
                    <p className="mt-2 flex items-center gap-2 text-sm text-muted" data-id="element-111" data-mp="2k6034qz3r6xm">
                      <MapPinIcon size={14} className="shrink-0 text-gold-dim" aria-hidden data-id="element-112" data-mp="2yumfcjtfnikm" />
                      {event.place}
                    </p>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted" data-id="element-113" data-mp="2tyxe8c1rltm7">{event.detail}</p>
                  </div>
                  <span className={`inline-flex w-fit border px-3 py-1 font-display text-[0.65rem] uppercase tracking-[0.14em] ${CATEGORY_STYLES[event.category]}`} data-id="element-114" data-mp="218fcbecs1yad">
                    {category?.label}
                  </span>
                </li>;
        })}
          </ul>}
      </section>

      <section className="border-t border-gold/15 bg-panel" data-id="element-115" data-mp="3mlrusxvgo8ue">
        <div className="mx-auto max-w-shell px-5 py-16 lg:px-8 lg:py-20" data-id="element-116" data-mp="3mncqwrxfbzud">
          <SectionHeading kanji="成果" title="Résultats sportifs" description="Les performances de nos compétiteurs, du départemental à l’international." data-id="element-117" data-mp="1w5g34516ge1e" />
          <ul className="mt-10 grid gap-px border border-gold/10 bg-gold/10 sm:grid-cols-2" data-id="element-118" data-mp="2539oikxeln9s">
            {RESULTS.map(result => <li key={`${result.athlete}-${result.year}`} className="flex items-start gap-4 bg-panel p-6" data-id="element-119" data-mp="2a1jmt4efd3zm">
                <TrophyIcon size={20} className="mt-1 shrink-0 text-gold" aria-hidden data-id="element-120" data-mp="2k23q0cli8ikh" />
                <div data-id="element-121" data-mp="2jzr11fhbx7xx">
                  <p className="font-display text-base uppercase tracking-[0.06em] text-bone" data-id="element-122" data-mp="2ot25j9yo5cmo">{result.athlete}</p>
                  <p className="mt-1 text-sm text-gold" data-id="element-123" data-mp="2suniswf47sxe">{result.rank}</p>
                  <p className="mt-1 text-sm text-muted" data-id="element-124" data-mp="2xti72dvuq9ow">
                    {result.event} · {result.year}
                  </p>
                </div>
              </li>)}
          </ul>
        </div>
      </section>
    </>;
}